import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import moment from 'moment/moment';
import {
    CreateChancel,
    Input,
    SelectAutocompleteMultiple,
    DateCalendarForm,
    MonthCalendarForm,
    ErrMessage,
    SelectAutocompletePagination,
    SelectAutocompletePaginationMultiple,
} from 'components';
import { useModal, Images, DownloadFile } from 'utils';
import { authService } from '../../../../store/reports/reports.service';
import { systemService } from '../../../../store/system/system.service';
import { clientActions } from 'store';

export const GenerateReport = ({ info }) => {
    const { close } = useModal();
    const dispatch = useDispatch();
    const { handleSubmit, watch, control, setError, clearErrors, reset, setValue } = useForm({
        defaultValues: info?.defaultValues || {},
    });
    const { loadDownload, handleDownload } = DownloadFile();
    const [load, setLoad] = useState(false);
    const { basicClients, fundingSourceList, adminsList, globalPayCodes } = useSelector((state) => ({
        basicClients: state.client.basicClients,
        fundingSourceList: state.fundingSource.fundingSourceList,
        adminsList: state.admins.adminsList,
        globalPayCodes: state.payroll.payCodes,
    }));
    const [backError, setBackError] = useState('');
    const [serviceTypes, setServiceTypes] = useState([]);
    const memoizedSendParams = useMemo(
        () => (info?.filterClients && watch('payer') ? { payer: watch('payer') } : null),
        [info?.filterClients, watch('payer')]
    );

    useEffect(() => {
        setBackError('');
        const needsServiceTypes = info?.list?.some((item) => item?.name === 'serviceTypeId');
        if (needsServiceTypes) {
            systemService.getServicesService().then((res) => {
                const list = (res?.data?.serviceTypes || []).map((st) => ({ id: st.id || st._id, name: st.name }));
                setServiceTypes(list);
            });
        }
    }, []);
    const handleGenerate = (data) => {
        setLoad(true);

        const params = {
            ...data,
        };
        params?.clientStatuses ? (params.clientStatuses = params?.clientStatuses?.map((i) => i?.id)) : null;

        params?.statuses ? (params.statuses = params?.statuses?.map((i) => i?.id)) : null;

        params?.payers ? (params.payers = params?.payers?.map((i) => i?.id)) : null;

        params?.apptTypes ? (params.apptTypes = params?.apptTypes?.map((i) => i?.id)) : null;

        // Multi-select of form templates (bulk note export) — the control hands
        // back option objects, the api wants ids. Dropped entirely when empty so
        // it doesn't read as "no form matches".
        if (params?.formIds) {
            const formIds = params.formIds.map((i) => i?.id ?? i).filter(Boolean);
            formIds.length ? (params.formIds = formIds) : delete params.formIds;
        }

        // Bulk note export only: its record filters are swapped per form type, so
        // the ones the current type doesn't show are left empty and must not travel.
        if (params?.formModel) {
            ['client', 'staff', 'resourceId'].forEach((key) => {
                if (!params[key]) delete params[key];
                else if (params[key]?.id) params[key] = params[key].id;
            });
        }

        params?.authorizationStatus
            ? (params.authorizationStatus = params?.authorizationStatus?.id || params?.authorizationStatus)
            : null;

        if (params?.type === undefined) delete params.type;

        if (info?.title === 'Signature Report' && params?.date) {
            params.date = moment.utc(data?.date).set('date', 2).format('YYYY-MM-DD');
            // params.date = moment.utc(data?.date).format('YYYY-MM-DD');
        }
        try {
            authService
                .getReportService(info?.url, params)
                .then((res) => {
                    close();
                    toast(
                        <div>
                            <p className="report-is-being-generated">
                                {'⏳ Report is being generated. You’ll be notified once it’s ready'}
                            </p>
                        </div>,
                        {
                            className: 'custom-toast-success',
                            position: 'top-center',
                            closeButton: true,
                            autoClose: 1500,
                        }
                    );
                })
                .catch((e) => {
                    setBackError(e?.data?.message);
                })
                .finally(() => {
                    setLoad(false);
                });
        } catch (e) {}
    };

    const renderList = (type, item) => {
        switch (type) {
            case 'clientIds':
                return basicClients;
            case 'client':
                return basicClients;
            case 'clients':
                return basicClients;
            case 'payer':
                return fundingSourceList?.funders;
            case 'payers':
                return fundingSourceList?.funders;
            case 'fundingSource':
                return fundingSourceList?.funders;
            case 'staff':
                return adminsList?.staffs;
            case 'staffs':
                return adminsList?.staffs;
            case 'payCodeTypeId':
                return globalPayCodes;
            case 'serviceTypeId':
                return serviceTypes;

            default:
                return item?.selectList;
        }
    };

    return (
        <div style={{ width: 440 }}>
            <p className="modal-header-title">{info?.title}</p>
            <form onSubmit={handleSubmit(handleGenerate)}>
                <div className="modal-body-wrapper">
                    {info?.list?.map((item, index) => (
                        <div key={index}>
                            {item?.type === 'text' ? (
                                <Input
                                    name={item?.name}
                                    label={item?.label}
                                    variant="outlined"
                                    control={control}
                                    type={item?.inputType ? item?.inputType : 'text'}
                                />
                            ) : item?.type === 'select' ? (
                                <SelectAutocompleteMultiple
                                    options={renderList(item?.name, item)}
                                    renderValue={(option) => item?.renderValue(option)}
                                    label={`${item?.label}${item?.required ? '*' : ''}`}
                                    name={item?.name}
                                    control={control}
                                    loadType={item?.loadType}
                                    rules={{
                                        required: {
                                            value: item?.required ? item?.required : false,
                                            message: `${item?.label} is required.`,
                                        },
                                    }}
                                />
                            ) : item?.type === 'selectMultipleWidthPagination' ? (
                                <SelectAutocompletePaginationMultiple
                                    uiType={'fromForm'}
                                    title={item?.label}
                                    name={item?.name}
                                    handleSelect={(e) => setValue(item?.name, e?.target?.value)}
                                    defaultValue={watch(item?.name)}
                                    renderValue={(i) => item?.renderValue(i)}
                                    type={item?.reqType}
                                />
                            ) : item?.type === 'selectWidthPagination' ? (
                                <SelectAutocompletePagination
                                    uiType={'fromForm'}
                                    title={item?.label}
                                    name={item?.name}
                                    handleSelect={(e) => setValue(item?.name, e?.target?.value)}
                                    defaultValue={watch(item?.name)}
                                    renderValue={(i) => item?.renderValue(i)}
                                    type={item?.reqType}
                                    sendParams={memoizedSendParams}
                                />
                            ) : item?.type === 'selectMultiple' ? (
                                <SelectAutocompleteMultiple
                                    loadType={item?.loadType}
                                    renderValue={(option) => item?.renderValue(option)}
                                    options={renderList(item?.name, item)}
                                    label={item?.label}
                                    name={item?.name}
                                    control={control}
                                    selectedType="id"
                                    multiple={true}
                                />
                            ) : item?.type === 'dateRange' ? (
                                <div className="flex-align-start" style={{ gap: 16 }}>
                                    <DateCalendarForm
                                        name={item?.startDate}
                                        label={item?.startLabel}
                                        max={watch(item?.endDate) ? moment.utc(watch(item?.endDate)).format('YYYY-MM-DD') : ''}
                                        control={control}
                                    />
                                    <DateCalendarForm
                                        name={item?.endDate}
                                        label={item?.endLabel}
                                        min={watch(item?.startDate) ? moment.utc(watch(item?.startDate)).format('YYYY-MM-DD') : ''}
                                        control={control}
                                    />
                                </div>
                            ) : item?.type === 'date' ? (
                                <DateCalendarForm name={item?.name} label={item?.label} control={control} />
                            ) : item?.type === 'dos' ? (
                                <div style={{ gap: 12 }} className="flex-align-center">
                                    <DateCalendarForm
                                        control={control}
                                        name={item?.startDate}
                                        label={`${item?.startLabel}${item?.required ? '*' : ''}`}
                                        max={watch(item?.endDate) ? moment.utc(watch(item?.endDate)).format('YYYY-MM-DD') : ''}
                                        rules={{
                                            required: {
                                                value: item?.required,
                                                message: `${item?.startLabel} is required.`,
                                            },
                                        }}
                                    />
                                    <DateCalendarForm
                                        control={control}
                                        name={item?.endDate}
                                        label={`${item?.endLabel}${item?.required ? '*' : ''}`}
                                        min={watch(item?.startDate) ? moment.utc(watch(item?.startDate)).format('YYYY-MM-DD') : ''}
                                        rules={{
                                            required: {
                                                value: item?.required,
                                                message: `${item?.endLabel} is required.`,
                                            },
                                        }}
                                    />
                                </div>
                            ) : item?.type === 'monthDos' ? (
                                <div style={{ gap: 12 }} className="flex-align-center">
                                    <MonthCalendarForm
                                        control={control}
                                        name={item?.startDate}
                                        label={`${item?.startLabel}${item?.required ? '*' : ''}`}
                                        max={watch(item?.endDate) ? moment.utc(watch(item?.endDate)).format('YYYY-MM-DD') : ''}
                                        rules={{
                                            required: {
                                                value: item?.required,
                                                message: `${item?.startLabel} is required.`,
                                            },
                                        }}
                                    />
                                    <MonthCalendarForm
                                        control={control}
                                        name={item?.endDate}
                                        label={`${item?.endLabel}${item?.required ? '*' : ''}`}
                                        min={watch(item?.startDate) ? moment.utc(watch(item?.startDate)).format('YYYY-MM-DD') : ''}
                                        rules={{
                                            required: {
                                                value: item?.required,
                                                message: `${item?.endLabel} is required.`,
                                            },
                                        }}
                                    />
                                </div>
                            ) : item?.type === 'monthDate' ? (
                                <div style={{ gap: 12 }} className="flex-align-center">
                                    <Input
                                        name={item?.name}
                                        label={`${item?.startLabel}${item?.required ? '*' : ''}`}
                                        placeholder={'MM/YYYY'}
                                        variant="outlined"
                                        control={control}
                                        type={'month'}
                                        rules={{
                                            required: {
                                                value: item?.required,
                                                message: `${item?.startLabel} is required.`,
                                            },
                                        }}
                                    />
                                </div>
                            ) : item?.type === 'customRow' ? (
                                <div>{item?.renderRow ? item?.renderRow(control, watch, setValue) : null}</div>
                            ) : (
                                <div />
                            )}
                        </div>
                    ))}
                    <ErrMessage text={backError} />
                </div>

                <div>
                    <CreateChancel
                        image={Images.generateReportWhite}
                        type="submit"
                        loader={load || loadDownload}
                        create={'Generate'}
                        chancel={'Cancel'}
                        onClose={close}
                        buttonWidth="212px"
                        gap={'16px'}
                        cancelRight={'0'}
                    />
                </div>
            </form>
        </div>
    );
};
